/**
 * Responsibility: the searches kept, as a table you can run from.
 *
 * A saved search is only worth keeping if running it again is one click, so
 * the whole row is the button. The filters are shown beside the phrase
 * because they are half of what the search was - two rows reading
 * "charizard" that differ only in a price floor are otherwise
 * indistinguishable.
 *
 * The thumbnails are a snapshot from the day it was saved, and the column
 * says so. Drawing current ones would cost a full search per row every time
 * this tab opened, against a free catalogue, to decorate a list.
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import BookmarkIcon from '@mui/icons-material/BookmarkBorder';
import PlayIcon from '@mui/icons-material/PlayArrowOutlined';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import { EMPTY } from '@kit/utils';
import type { SavedSearch } from '../../api/client';
import { money } from '../grading/money';
import { RotatingThumb } from './RotatingThumb';

type SavedSearchesTabProps = {
  searches: SavedSearch[];
  busy: boolean;
  /** Given the whole row, so the page can record that it was run. */
  onRun: (entry: SavedSearch) => void;
  onForget: (searchId: string) => void;
  maxHeight?: number;
};

const THUMB_WIDTH = 52;

/** The filters as short chips, so two similar searches read differently. */
function Filters({ entry }: { entry: SavedSearch }) {
  const chips: string[] = [entry.mode === 'ARBITRAGE' ? 'Arbitrage' : 'Top value'];
  if (entry.category) chips.push(entry.category === 'SPORTS' ? 'Sports' : 'Trading cards');
  if (entry.max_raw) chips.push(`raw under ${money(entry.max_raw)}`);
  if (entry.min_value) chips.push(`worth over ${money(entry.min_value)}`);

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {chips.map((label) => (
        <Chip key={label} size="small" variant="outlined" label={label} />
      ))}
    </Stack>
  );
}

function Empty() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, minHeight: 240, px: 3 }}>
      <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', maxWidth: 460 }}>
        <BookmarkIcon sx={{ fontSize: 34, color: 'text.disabled' }} />
        <Typography variant="body1" color="text.secondary">
          No saved searches yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Run a search, then use Save beside the filters. The phrase and the filters are
          kept together, so running it again asks the same question.
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * Column widths are measured, not guessed: headings never wrap, so a
 * heading that does not fit its column is clipped. `tests/table-headers.mjs`
 * renders this table in a browser and fails on a clipped one.
 */
export function SavedSearchesTab({
  searches,
  busy,
  onRun,
  onForget,
  maxHeight,
}: SavedSearchesTabProps) {
  if (searches.length === 0) return <Empty />;

  const columns: DataColumn<SavedSearch>[] = [
    {
      id: 'search',
      label: 'Search',
      render: (entry) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, py: 0.5 }}>
          <span>
            <RotatingThumb
              images={entry.preview_images}
              alt={entry.label}
              width={THUMB_WIDTH}
            />
          </span>

          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {entry.label}
            </Typography>
            <Filters entry={entry} />
          </Stack>
        </Stack>
      ),
      sortValue: (entry) => entry.label,
    },
    {
      id: 'found',
      label: 'Found then',
      width: 130,
      align: 'right',
      render: (entry) => (
        <Typography
          variant="body2"
          color={entry.result_count === null ? 'text.disabled' : 'text.primary'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {entry.result_count === null ? EMPTY : entry.result_count}
        </Typography>
      ),
      sortValue: (entry) => entry.result_count ?? -1,
    },
    {
      id: 'saved',
      label: 'Saved',
      width: 110,
      render: (entry) => (
        <Typography variant="caption" color="text.secondary">
          {entry.created_at.slice(0, 10)}
        </Typography>
      ),
      sortValue: (entry) => entry.created_at,
    },
    {
      id: 'last',
      label: 'Last run',
      width: 110,
      render: (entry) => (
        <Typography variant="caption" color="text.secondary">
          {entry.last_run_at ? entry.last_run_at.slice(0, 10) : 'never'}
        </Typography>
      ),
      sortValue: (entry) => entry.last_run_at ?? '',
    },
    {
      id: 'actions',
      label: '',
      width: 92,
      align: 'right',
      render: (entry) => (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
          <ToolbarIconButton
            aria-label="Run this search again"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onRun(entry);
            }}
          >
            <PlayIcon fontSize="small" />
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Forget this search"
            disabled={busy}
            onClick={(event) => {
              // The row runs the search, so the delete has to stop the
              // click before it gets there.
              event.stopPropagation();
              onForget(entry.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </ToolbarIconButton>
        </Stack>
      ),
    },
  ];

  return (
    <DataTable
      rows={searches}
      columns={columns}
      rowKey={(entry) => entry.id}
      onRowClick={onRun}
      maxHeight={maxHeight}
      emptyMessage="No saved searches yet."
    />
  );
}
